import { Fragment } from "react";
import { Building2, ChevronRight, HelpCircle, LockKeyhole } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useOutlet } from "@/contexts/OutletContext";

interface AdminLayoutProps {
  title: string;
  /** Optional root crumb (a top-level nav destination, e.g. "Home"). */
  parent?: string;
  /** When set, the parent crumb links to this ?tab= section. */
  parentSection?: string;
  middle?: string;
  middleSection?: string;
  fitViewport?: boolean;
  /** Removes the content inset so a workspace can occupy the full area below the header. */
  flushContent?: boolean;
  /** Shows the "Help" affordance in the top-right of the header row. */
  showHelp?: boolean;
  onHelp?: () => void;
  children: React.ReactNode;
}

const AdminLayout = ({ title, parent, parentSection, middle, middleSection, fitViewport = false, flushContent = false, showHelp = false, onHelp, children }: AdminLayoutProps) => {
  const navigate = useNavigate();
  const { isAllOutlets } = useOutlet();

  // Leading crumbs before the (current) title. Each is clickable when it has a
  // section to navigate to. Top-level tabs pass no parent/middle, so the
  // breadcrumb collapses to just the title.
  type Crumb = { label: string; section?: string };
  const crumbs = ([
    parent ? { label: parent, section: parentSection } : null,
    middle ? { label: middle, section: middleSection } : null,
  ] as (Crumb | null)[]).filter((c): c is Crumb => c !== null);

  return (
    <div className={fitViewport ? "flex flex-col h-full overflow-hidden" : "flex flex-col pb-8"}>
      {/* Breadcrumb with sidebar trigger, spanning full width with a divider below.
          Fixed h-12 so the row lines up with the sidebar's branch switcher band. */}
      <div className="admin-primary-bar">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <SidebarTrigger className="-ml-1.5" />
          {crumbs.map((c) => (
            <Fragment key={c.label}>
              {c.section ? (
                <span
                  onClick={() => navigate(`/?tab=${c.section}`)}
                  className="hover:text-foreground cursor-pointer"
                >
                  {c.label}
                </span>
              ) : (
                <span>{c.label}</span>
              )}
              <ChevronRight className="w-4 h-4" />
            </Fragment>
          ))}
          <span className="text-foreground font-medium">{title}</span>
        </div>

        {showHelp && (
          <button
            type="button"
            onClick={onHelp}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <HelpCircle className="h-4 w-4" />
            Help
          </button>
        )}
      </div>

      {isAllOutlets && (
        <div className="flex items-center gap-2 border-b border-border bg-muted/35 px-4 py-2 text-xs text-muted-foreground md:px-6">
          <Building2 className="h-4 w-4 shrink-0 text-primary" />
          <span className="font-medium text-foreground">All outlets</span>
          <span aria-hidden="true">·</span>
          <LockKeyhole className="h-3.5 w-3.5 shrink-0" />
          <span>Read-only overview. Select an outlet to make changes.</span>
        </div>
      )}

      {fitViewport ? (
        <div className={flushContent
          ? "flex-1 min-h-0"
          : "flex-1 min-h-0 px-4 md:px-6 pt-4 pb-4 md:pb-6"
        }>
          {children}
        </div>
      ) : (
        <div className="px-4 md:px-6 pt-4">{children}</div>
      )}
    </div>
  );
};

export default AdminLayout;
